import { createContext, useContext, useReducer, useEffect, type ReactNode } from 'react';
import type { User, Team, Board, Task, Sprint, Label, Page, ViewType, TaskStatus, Comment, Attachment, SubTask, BoardColumn, UserRole } from '../types';
import * as api from '../services/api';
import { getUserData } from './../../../modules/auth'

interface AppState {
  currentUser: User | null;
  users: User[];
  teams: Team[];
  boards: Board[];
  tasks: Task[];
  sprints: Sprint[];
  labels: Label[];
  currentPage: Page;
  currentBoardId: string;
  currentView: ViewType;
  selectedTaskId: string | null;
  sidebarOpen: boolean;
  searchQuery: string;
  loading: boolean;
  error: string | null;
}

type Action =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'INIT_DATA'; payload: { users: User[]; teams: Team[]; boards: Board[]; tasks: Task[]; sprints: Sprint[]; labels: Label[] } }
  | { type: 'SET_PAGE'; payload: Page }
  | { type: 'SET_BOARD'; payload: string }
  | { type: 'SET_VIEW'; payload: ViewType }
  | { type: 'SELECT_TASK'; payload: string | null }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SIDEBAR_OPEN'; payload: boolean }
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'UPDATE_TASK'; payload: Task }
  | { type: 'ADD_TASK'; payload: Task }
  | { type: 'DELETE_TASK'; payload: string }
  | { type: 'MOVE_TASK'; payload: { taskId: string; status: TaskStatus } }
  | { type: 'ADD_COMMENT'; payload: { taskId: string; comment: Comment } }
  | { type: 'ADD_ATTACHMENT'; payload: { taskId: string; attachment: Attachment } }
  | { type: 'REMOVE_ATTACHMENT'; payload: { taskId: string; attachmentId: string } }
  | { type: 'UPDATE_SPRINT'; payload: Sprint }
  | { type: 'ADD_SPRINT'; payload: Sprint }
  | { type: 'DELETE_SPRINT'; payload: string }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'ADD_TEAM'; payload: Team }
  | { type: 'UPDATE_TEAM'; payload: Team }
  | { type: 'DELETE_TEAM'; payload: string }
  | { type: 'ADD_BOARD'; payload: Board }
  | { type: 'UPDATE_BOARD'; payload: Board }
  | { type: 'DELETE_BOARD'; payload: string }
  | { type: 'ADD_LABEL'; payload: Label }
  | { type: 'UPDATE_LABEL'; payload: Label }
  | { type: 'DELETE_LABEL'; payload: string };

const initialState: AppState = {
  currentUser: null,
  users: [],
  teams: [],
  boards: [],
  tasks: [],
  sprints: [],
  labels: [],
  currentPage: 'dashboard',
  currentBoardId: '',
  currentView: 'kanban',
  selectedTaskId: null,
  sidebarOpen: false,
  searchQuery: '',
  loading: true,
  error: null,
};

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'INIT_DATA':
      return {
        ...state,
        users: action.payload.users,
        teams: action.payload.teams,
        boards: action.payload.boards,
        tasks: action.payload.tasks,
        sprints: action.payload.sprints,
        labels: action.payload.labels,
        currentUser: getUserData() || null,
        currentBoardId: action.payload.boards[0]?.id || '',
        loading: false,
      };
    case 'SET_PAGE':
      return { ...state, currentPage: action.payload, selectedTaskId: null };
    case 'SET_BOARD':
      return { ...state, currentBoardId: action.payload };
    case 'SET_VIEW':
      return { ...state, currentView: action.payload };
    case 'SELECT_TASK':
      return { ...state, selectedTaskId: action.payload };
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarOpen: !state.sidebarOpen };
    case 'SET_SIDEBAR_OPEN':
      return { ...state, sidebarOpen: action.payload };
    case 'SET_SEARCH':
      return { ...state, searchQuery: action.payload };
    case 'UPDATE_TASK':
      return {
        ...state,
        tasks: state.tasks.map((t) => (t.id === action.payload.id ? action.payload : t)),
      };
    case 'ADD_TASK':
      return { ...state, tasks: [...state.tasks, action.payload] };
    case 'DELETE_TASK':
      return {
        ...state,
        tasks: state.tasks.filter((t) => t.id !== action.payload),
        selectedTaskId: state.selectedTaskId === action.payload ? null : state.selectedTaskId,
      };
    case 'MOVE_TASK':
      return {
        ...state,
        tasks: state.tasks.map((t) =>
          t.id === action.payload.taskId
            ? { ...t, status: action.payload.status, updatedAt: new Date().toISOString() }
            : t
        ),
      };
    case 'ADD_COMMENT':
      return {
        ...state,
        tasks: state.tasks.map((t) =>
          t.id === action.payload.taskId
            ? { ...t, comments: [...t.comments, action.payload.comment], updatedAt: new Date().toISOString() }
            : t
        ),
      };
    case 'ADD_ATTACHMENT':
      return {
        ...state,
        tasks: state.tasks.map((t) =>
          t.id === action.payload.taskId
            ? { ...t, attachments: [...t.attachments, action.payload.attachment], updatedAt: new Date().toISOString() }
            : t
        ),
      };
    case 'REMOVE_ATTACHMENT':
      return {
        ...state,
        tasks: state.tasks.map((t) =>
          t.id === action.payload.taskId
            ? {
                ...t,
                attachments: t.attachments.filter((a) => a.id !== action.payload.attachmentId),
                updatedAt: new Date().toISOString(),
              }
            : t
        ),
      };
    case 'UPDATE_SPRINT':
      return {
        ...state,
        sprints: state.sprints.map((s) => (s.id === action.payload.id ? action.payload : s)),
      };
    case 'ADD_SPRINT':
      return { ...state, sprints: [...state.sprints, action.payload] };
    case 'DELETE_SPRINT':
      return {
        ...state,
        sprints: state.sprints.filter((s) => s.id !== action.payload),
        tasks: state.tasks.map((t) =>
          t.sprintId === action.payload ? { ...t, sprintId: null } : t
        ),
      };
    case 'UPDATE_USER':
      return {
        ...state,
        users: state.users.map((u) => (u.id === action.payload.id ? action.payload : u)),
        currentUser: state.currentUser?.id_user === action.payload.id ? action.payload : state.currentUser,
      };
    case 'ADD_TEAM':
      return { ...state, teams: [...state.teams, action.payload] };
    case 'UPDATE_TEAM':
      return {
        ...state,
        teams: state.teams.map((t) => (t.id === action.payload.id ? action.payload : t)),
      };
    case 'DELETE_TEAM':
      return {
        ...state,
        teams: state.teams.filter((t) => t.id !== action.payload),
      };
    case 'ADD_BOARD':
      return { ...state, boards: [...state.boards, action.payload] };
    case 'UPDATE_BOARD':
      return {
        ...state,
        boards: state.boards.map((b) => (b.id === action.payload.id ? action.payload : b)),
      };
    case 'DELETE_BOARD':
      return {
        ...state,
        boards: state.boards.filter((b) => b.id !== action.payload),
        currentBoardId: state.currentBoardId === action.payload
          ? (state.boards.find((b) => b.id !== action.payload)?.id ?? '')
          : state.currentBoardId,
      };
    case 'ADD_LABEL':
      return { ...state, labels: [...state.labels, action.payload] };
    case 'UPDATE_LABEL':
      return {
        ...state,
        labels: state.labels.map((l) => (l.id === action.payload.id ? action.payload : l)),
      };
    case 'DELETE_LABEL':
      return {
        ...state,
        labels: state.labels.filter((l) => l.id !== action.payload),
      };
    default:
      return state;
  }
}

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<Action>;
  getUserById: (id: string) => User | undefined;
  getTeamById: (id: string) => Team | undefined;
  getBoardById: (id: string) => Board | undefined;
  getLabelById: (id: string) => Label | undefined;
  getTasksForBoard: (boardId: string) => Task[];
  getTasksForSprint: (sprintId: string) => Task[];
  apiCreateTask: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  apiUpdateTask: (task: Partial<Task> & { id: string }) => Promise<void>;
  apiDeleteTask: (id: string) => Promise<void>;
  apiMoveTask: (taskId: string, status: TaskStatus) => Promise<void>;
  apiAddComment: (taskId: string, content: string, attachments?: { name: string; size: number; type: string; id_upload: number }[]) => Promise<void>;
  apiAddAttachment: (taskId: string, attachment: { name: string; size: number; type: string; id_upload: number }) => Promise<void>;
  apiRemoveAttachment: (id: string) => Promise<void>;
  apiAddSubtask: (taskId: string, subtask: Omit<SubTask, 'id'>) => Promise<void>;
  apiUpdateSubtask: (subtask: Partial<SubTask> & { id: string }) => Promise<void>;
  apiDeleteSubtask: (id: string) => Promise<void>;
  apiCreateBoard: (board: Omit<Board, 'id' | 'createdAt'>) => Promise<void>;
  apiUpdateBoard: (board: Partial<Board> & { id: string }) => Promise<void>;
  apiDeleteBoard: (id: string) => Promise<void>;
  apiUpdateBoardColumns: (boardId: string, columns: BoardColumn[]) => Promise<void>;
  apiCreateTeam: (team: Omit<Team, 'id' | 'createdAt'>) => Promise<void>;
  apiUpdateTeam: (team: Team) => Promise<void>;
  apiDeleteTeam: (id: string) => Promise<void>;
  apiCreateSprint: (sprint: Omit<Sprint, 'id'>) => Promise<void>;
  apiUpdateSprint: (sprint: Partial<Sprint> & { id: string }) => Promise<void>;
  apiDeleteSprint: (id: string) => Promise<void>;
  apiUpdateUserRole: (userId: string, role: string) => Promise<void>;
  apiCreateLabel: (label: Omit<Label, 'id'>) => Promise<void>;
  apiUpdateLabel: (label: Label) => Promise<void>;
  apiDeleteLabel: (id: string) => Promise<void>;
}

const AppContext = createContext<AppContextType | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  useEffect(() => {
    async function loadInitialData() {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const [users, teams, boards, tasks, sprints, labels] = await Promise.all([
          api.fetchUsers(),
          api.fetchTeams(),
          api.fetchBoards(),
          api.fetchTasks(),
          api.fetchSprints(),
          api.fetchLabels(),
        ]);
        dispatch({ type: 'INIT_DATA', payload: { users, teams, boards, tasks, sprints, labels } });
      } catch (error) {
        console.error('Failed to load initial data:', error);
        dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to load data' });
      }
    }
    loadInitialData();
  }, []);

  const getUserById = (id: string) => state.users.find((u) => u.id === id);
  const getTeamById = (id: string) => state.teams.find((t) => t.id === id);
  const getBoardById = (id: string) => state.boards.find((b) => b.id === id);
  const getLabelById = (id: string) => state.labels.find((l) => l.id === id);

  const getTasksForBoard = (boardId: string) => {
    const filtered = state.tasks.filter((t) => t.boardId === boardId);
    if (!state.searchQuery) return filtered;
    const q = state.searchQuery.toLowerCase();
    return filtered.filter(
      (t) => t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q)
    );
  };

  const getTasksForSprint = (sprintId: string) =>
    state.tasks.filter((t) => t.sprintId === sprintId);

  const apiCreateTask = async (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newTask = await api.createTask(task);
    dispatch({ type: 'ADD_TASK', payload: newTask });
  };

  const apiUpdateTask = async (task: Partial<Task> & { id: string }) => {
    const updatedTask = await api.updateTask(task);
    dispatch({ type: 'UPDATE_TASK', payload: updatedTask });
  };

  const apiDeleteTask = async (id: string) => {
    await api.deleteTask(id);
    dispatch({ type: 'DELETE_TASK', payload: id });
  };

  const apiMoveTask = async (taskId: string, status: TaskStatus) => {
    await api.moveTask(taskId, status);
    dispatch({ type: 'MOVE_TASK', payload: { taskId, status } });
  };

  const apiAddComment = async (taskId: string, content: string, attachments?: { name: string; size: number; type: string; id_upload: number }[]) => {
    const comment = await api.addComment(taskId, content, attachments);
    dispatch({ type: 'ADD_COMMENT', payload: { taskId, comment } });
  };

  const apiAddAttachment = async (taskId: string, attachment: { name: string; size: number; type: string; id_upload: number }) => {
    const newAttachment = await api.addAttachment(taskId, attachment);
    dispatch({ type: 'ADD_ATTACHMENT', payload: { taskId, attachment: newAttachment } });
  };

  const apiRemoveAttachment = async (id: string) => {
    await api.removeAttachment(id);
    const task = state.tasks.find((t) => t.attachments.some((a) => a.id === id));
    if (task) {
      dispatch({ type: 'REMOVE_ATTACHMENT', payload: { taskId: task.id, attachmentId: id } });
    }
  };

  const apiAddSubtask = async (taskId: string, subtask: Omit<SubTask, 'id'>) => {
    const newSubtask = await api.addSubtask(taskId, subtask);
    const task = state.tasks.find((t) => t.id === taskId);
    if (task) {
      const updatedTask = { ...task, subtasks: [...task.subtasks, newSubtask] };
      dispatch({ type: 'UPDATE_TASK', payload: updatedTask });
    }
  };

  const apiUpdateSubtask = async (subtask: Partial<SubTask> & { id: string }) => {
    await api.updateSubtask(subtask);
    const task = state.tasks.find((t) => t.subtasks.some((s) => s.id === subtask.id));
    if (task) {
      const updatedTask = {
        ...task,
        subtasks: task.subtasks.map((s) => (s.id === subtask.id ? { ...s, ...subtask } : s)),
      };
      dispatch({ type: 'UPDATE_TASK', payload: updatedTask });
    }
  };

  const apiDeleteSubtask = async (id: string) => {
    await api.deleteSubtask(id);
    const task = state.tasks.find((t) => t.subtasks.some((s) => s.id === id));
    if (task) {
      const updatedTask = {
        ...task,
        subtasks: task.subtasks.filter((s) => s.id !== id),
      };
      dispatch({ type: 'UPDATE_TASK', payload: updatedTask });
    }
  };

  const apiCreateBoard = async (board: Omit<Board, 'id' | 'createdAt'>) => {
    const newBoard = await api.createBoard(board);
    dispatch({ type: 'ADD_BOARD', payload: newBoard });
  };

  const apiUpdateBoard = async (board: Partial<Board> & { id: string }) => {
    const updatedBoard = await api.updateBoard(board);
    dispatch({ type: 'UPDATE_BOARD', payload: updatedBoard });
  };

  const apiDeleteBoard = async (id: string) => {
    await api.deleteBoard(id);
    dispatch({ type: 'DELETE_BOARD', payload: id });
  };

  const apiUpdateBoardColumns = async (boardId: string, columns: BoardColumn[]) => {
    const updatedBoard = await api.updateBoardColumns(boardId, columns);
    dispatch({ type: 'UPDATE_BOARD', payload: updatedBoard });
  };

  const apiCreateTeam = async (team: Omit<Team, 'id' | 'createdAt'>) => {
    const newTeam = await api.createTeam(team);
    dispatch({ type: 'ADD_TEAM', payload: newTeam });
  };

  const apiUpdateTeam = async (team: Team) => {
    const updatedTeam = await api.updateTeam(team);
    dispatch({ type: 'UPDATE_TEAM', payload: updatedTeam });
  };

  const apiDeleteTeam = async (id: string) => {
    await api.deleteTeam(id);
    dispatch({ type: 'DELETE_TEAM', payload: id });
  };

  const apiCreateSprint = async (sprint: Omit<Sprint, 'id'>) => {
    const newSprint = await api.createSprint(sprint);
    dispatch({ type: 'ADD_SPRINT', payload: newSprint });
  };

  const apiUpdateSprint = async (sprint: Partial<Sprint> & { id: string }) => {
    const updatedSprint = await api.updateSprint(sprint);
    dispatch({ type: 'UPDATE_SPRINT', payload: updatedSprint });
  };

  const apiDeleteSprint = async (id: string) => {
    await api.deleteSprint(id);
    dispatch({ type: 'DELETE_SPRINT', payload: id });
  };

  const apiUpdateUserRole = async (userId: string, role: string) => {
    await api.updateUserRole(userId, role);
    const user = state.users.find((u) => u.id === userId);
    if (user) {
      dispatch({ type: 'UPDATE_USER', payload: { ...user, role: role as UserRole } });
    }
  };

  const apiCreateLabel = async (label: Omit<Label, 'id'>) => {
    const newLabel = await api.createLabel(label);
    dispatch({ type: 'ADD_LABEL', payload: newLabel });
  };

  const apiUpdateLabel = async (label: Label) => {
    await api.updateLabel(label);
    dispatch({ type: 'UPDATE_LABEL', payload: label });
  };

  const apiDeleteLabel = async (id: string) => {
    await api.deleteLabel(id);
    dispatch({ type: 'DELETE_LABEL', payload: id });
  };

  return (
    <AppContext.Provider
      value={{
        state,
        dispatch,
        getUserById,
        getTeamById,
        getBoardById,
        getLabelById,
        getTasksForBoard,
        getTasksForSprint,
        apiCreateTask,
        apiUpdateTask,
        apiDeleteTask,
        apiMoveTask,
        apiAddComment,
        apiAddAttachment,
        apiRemoveAttachment,
        apiAddSubtask,
        apiUpdateSubtask,
        apiDeleteSubtask,
        apiCreateBoard,
        apiUpdateBoard,
        apiDeleteBoard,
        apiUpdateBoardColumns,
        apiCreateTeam,
        apiUpdateTeam,
        apiDeleteTeam,
        apiCreateSprint,
        apiUpdateSprint,
        apiDeleteSprint,
        apiUpdateUserRole,
        apiCreateLabel,
        apiUpdateLabel,
        apiDeleteLabel,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
}

export function useOptionalApp() {
  return useContext(AppContext);
}
